import styled, { css } from "styled-components";
import { Link } from "wouter";
import { QUERIES, SPACING } from "../../../../utils/constants";
import { NavRowProps } from "./types";

export const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;

  @media ${QUERIES.tabletLargeAndUp} {
    gap: ${SPACING.xs}px;
  }
`;

export const NavLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

export const IconBox = styled.div`
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background-color: var(--color-info-background);
  color: var(--color-primary);

  svg {
    font-size: var(--text-xxl);
  }

  @media ${QUERIES.tabletLargeAndUp} {
    width: 2rem;
    height: 2rem;

    svg {
      font-size: var(--text-xl);
    }
  }
`;

export const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const RowTitle = styled.span`
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--color-text-default);

  @media ${QUERIES.tabletLargeAndUp} {
    font-size: var(--text-base);
  }
`;

export const RowSubtitle = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

/* Chevron is mobile-only (menu affordance); hidden in the desktop sidebar */
export const Chevron = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  color: var(--color-text-tertiary);

  svg {
    font-size: var(--text-xxl);
  }

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

/* Desktop sidebar: active row highlight carried by a primary-tinted card plus a
   green icon and label. Two signals are enough; a left accent bar on top of the
   tint would only add noise. */
const desktopActive = css`
  background: var(--color-card-metric-background);
  border-color: var(--color-card-metric-border);

  ${IconBox} {
    color: var(--color-primary);
  }

  ${RowTitle} {
    color: var(--color-primary);
  }

  &:hover {
    background: var(--color-card-metric-background);
  }
`;

const desktopInactive = css`
  &:hover {
    background: var(--color-card-metric-background);
  }
`;

export const NavRow = styled.div<NavRowProps>`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  border-radius: 0.75rem;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  box-shadow: var(--shadow-small-1);
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  /* Desktop sidebar: compact rows, plain icons, active highlight, no card chrome */
  @media ${QUERIES.tabletLargeAndUp} {
    gap: ${SPACING.sm}px;
    padding: ${SPACING.sm}px ${SPACING.md}px;
    box-shadow: none;
    border-color: transparent;
    background: transparent;

    ${IconBox} {
      background: transparent;
      color: var(--color-text-secondary);
    }

    ${(props) => (props.$isActive ? desktopActive : desktopInactive)}
  }
`;
